import React from 'react';
import { Box, IconButton, Select, MenuItem, FormControl } from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { PaginationOptions } from '../../../../models/pagination';

interface ProjectListSortProps {
  pagination: PaginationOptions;
  onSortFieldChange: (sortField: string) => void;
  onSortDirectionToggle: () => void;
  isUploading?: boolean;
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Project Name' },
  { value: 'folder', label: 'Root Folder' },
  { value: 'dawType', label: 'DAW Type' },
  { value: 'size', label: 'File Size' },
  { value: 'created', label: 'Creation Date' },
  { value: 'updated', label: 'Update Date' },
];

const ProjectListSort: React.FC<ProjectListSortProps> = ({
  pagination,
  onSortFieldChange,
  onSortDirectionToggle,
  isUploading = false,
}) => {
  return (
    <Box
      display="flex"
      sx={{
        position: 'relative',
        height: '30.75px', // Match small button height (6px top + 18.75px content + 6px bottom = 30.75px including border)
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        },
        '&:hover .MuiIconButton-root': {
          borderColor: 'primary.main',
        },
        '& .MuiFormControl-root': {
          height: '100%',
          display: 'flex',
        },
        '& .MuiOutlinedInput-root': {
          height: '100%',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          '& .MuiOutlinedInput-notchedOutline': {
            borderRight: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderRight: 'none',
            borderColor: 'primary.main',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderRight: 'none',
            borderColor: 'primary.main',
          },
        },
        '&.Mui-focused': {
          '& .MuiIconButton-root': {
            borderColor: 'primary.main',
          },
        },
        '& .MuiIconButton-root': {
          height: '100%',
          width: '32px',
          minWidth: '32px',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderLeft: 'none',
          marginLeft: 0,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Match OutlinedInput default border color exactly
          borderColor: 'studio.borders.control',
          '&:hover': {
            borderLeft: 'none',
            borderColor: 'primary.main',
          },
        },
        // Ensure the OutlinedInput border matches the IconButton border
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'studio.borders.control',
        },
      }}
    >
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          variant="outlined"
          id="sort-field-select"
          value={pagination.sortField || 'name'}
          onChange={(e) => onSortFieldChange(e.target.value)}
          disabled={isUploading}
          sx={{
            height: '100%',
            '& .MuiSelect-select': {
              padding: '6px 14px',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            },
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        size="small"
        onClick={onSortDirectionToggle}
        disabled={isUploading}
        aria-label={pagination.ascending ? 'Sort ascending' : 'Sort descending'}
      >
        {pagination.ascending ? (
          <ArrowUpwardIcon fontSize="small" />
        ) : (
          <ArrowDownwardIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
};

export default ProjectListSort;
